import { Icon } from "@blueprintjs/core";
import { ItemStatus, SelectMenu, StatusProxy } from "@focus/focusUI";
import { useCallback, useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  ContactsByOperatorButton,
  type ContactsByOperatorSelection,
} from "../components/ContactsByOperatorButton";
import { ConjunctionContactsPanel } from "../components/conjunctions/ConjunctionContactsPanel";
import { FleetStatusCardList } from "../components/fleet/FleetStatusCardList";
import { Table } from "../components/table/Table";
import {
  TABLE_ACTION,
  type TableActionType,
} from "../components/table/TableActionColumn";
import { TableShell } from "../components/table/TableShell";
import { createFormattersObject } from "../helpers/utils";
import {
  fleetConjunctionsColumnsLayout,
  useAvailableconjunctionHeaders,
  useSavedFleetConjunctionColumns,
} from "../services/configuration";
import { fleetConjunctions } from "../services/conjunctions";
import { CONJ_DEFAULT_VISIBLE_COLUMNS } from "../services/conjunctions/Headers";
import { downloadFileZip } from "../services/downloaduploadOrbits/actions";
import {
  getColumnDefinitions,
  type ServerColumnConfig,
} from "../services/helpers/headers";
import type { ColumnFiltersState } from "../services/helpers/pager";
import type { RootState } from "../store";
import { RiskLevel } from "../types/conjunctions";
import { combineErrors, combineStatuses } from "../utils";
import { sortAlphabetically } from "../utils/sorting";
import { useMe } from "../hooks/useMe";
import { useSystemConfig } from "../hooks/useSystemConfig";

type FleetViewMode = "table" | "cards";

const viewModeOptions: Array<{
  icon: "th-list" | "grid-view";
  label: string;
  value: FleetViewMode;
}> = [
  { icon: "grid-view", label: "Cards", value: "cards" },
  { icon: "th-list", label: "Table", value: "table" },
];

export const FleetContainer: React.FC = () => {
  const dispatch = useDispatch();
  const me = useMe();
  const { currentConfig, loadConfig } = useSystemConfig();
  const [contactsSelection, setContactsSelection] =
    useState<ContactsByOperatorSelection | null>(null);

  const fleetConjunctionActions = fleetConjunctions.actions;
  const fleetConjunctionsLayoutActions = fleetConjunctionsColumnsLayout.actions;

  // Request the user active columns to the server's DB
  const availableHeaders = useAvailableconjunctionHeaders();
  const savedColumnsLayout = useSavedFleetConjunctionColumns();
  const columnDefinitions = useMemo(
    () => getColumnDefinitions(availableHeaders.value),
    [availableHeaders.value],
  );
  const formatters = useMemo(
    () => createFormattersObject(availableHeaders.value),
    [availableHeaders.value],
  );

  const saveColumnLayout = (serverConfig: ServerColumnConfig) => {
    dispatch(fleetConjunctionsLayoutActions.saveItems(serverConfig));
  };

  const fleetFilter = useSelector(
    (state: RootState) => state.fleetConjunctions.filter,
    shallowEqual,
  );

  const handleFleetFilterChange = useCallback(
    (filtered: ColumnFiltersState) =>
      dispatch(fleetConjunctionActions.setFilter(filtered)),
    [dispatch, fleetConjunctionActions],
  );

  const fleetStatus = useSelector((state: RootState) => {
    const slice = state.fleetConjunctions;

    return {
      status: fleetConjunctions.selectors.getStatus(slice),
      error: fleetConjunctions.selectors.getError(slice),
      items: fleetConjunctions.selectors.getItems(slice),
    };
  }, shallowEqual);

  const [selectedOrganization, setSelectedOrganization] = useState<
    string | undefined
  >(undefined);
  const [viewMode, setViewMode] = useState<FleetViewMode>("cards");
  const sortedOrganizations = useMemo(
    () => sortAlphabetically(me?.organizations ?? []),
    [me?.organizations],
  );

  useEffect(() => {
    if (selectedOrganization) {
      loadConfig(selectedOrganization);
    }
  }, [loadConfig, selectedOrganization]);

  const handleDownload = useCallback(
    (payload: { id: string }) => dispatch(downloadFileZip(payload)),
    [dispatch],
  );
  const handleContactsSelection = useCallback(
    (selection: ContactsByOperatorSelection) => {
      setContactsSelection(selection);
    },
    [],
  );
  const fleetTableActions = useMemo<TableActionType[]>(
    () => [
      TABLE_ACTION.DOWNLOAD,
      TABLE_ACTION.INFO,
      TABLE_ACTION.EVENT,
      TABLE_ACTION.PLOTS,
      (data) => (
        <ContactsByOperatorButton
          data={data}
          onSelect={handleContactsSelection}
        />
      ),
    ],
    [handleContactsSelection],
  );

  useEffect(() => {
    if (sortedOrganizations.length === 0) {
      setSelectedOrganization(undefined);
      return;
    }

    if (
      !selectedOrganization ||
      !sortedOrganizations.includes(selectedOrganization)
    ) {
      setSelectedOrganization(sortedOrganizations[0]);
    }
  }, [sortedOrganizations, selectedOrganization]);

  useEffect(() => {
    if (!selectedOrganization) return;

    setContactsSelection(null);
    dispatch(
      fleetConjunctionActions.fetchItems({
        organization: selectedOrganization,
      }),
    );
  }, [dispatch, fleetConjunctionActions, selectedOrganization]);

  const combinedStatus = combineStatuses([
    availableHeaders.status,
    savedColumnsLayout.status,
    fleetStatus.status,
  ]);
  const combinedError = combineErrors([
    availableHeaders.error,
    savedColumnsLayout.error,
    fleetStatus.error,
  ]);

  const fleetConjunctionItems = useMemo(() => {
    if (fleetStatus.status !== ItemStatus.READY) {
      return [] as unknown[];
    }

    const items = fleetStatus.items;

    if (!Array.isArray(items)) {
      return Array.isArray(items?.fleetConjunctions)
        ? items.fleetConjunctions
        : [];
    }

    if (items.some((item) => Array.isArray(item?.fleetConjunctions))) {
      return items.flatMap((item) => item?.fleetConjunctions ?? []);
    }

    return items;
  }, [fleetStatus.status, fleetStatus.items]);

  const riskSummary = useMemo(() => {
    return fleetConjunctionItems.reduce(
      (acc, item: any) => {
        const riskLevel = item?.riskLevel?.value;

        if (riskLevel === RiskLevel.ALERT) {
          acc.alerts += 1;
        } else if (riskLevel === RiskLevel.WARNING) {
          acc.warnings += 1;
        } else {
          acc.info += 1;
        }

        return acc;
      },
      { alerts: 0, warnings: 0, info: 0 },
    );
  }, [fleetConjunctionItems]);

  return (
    <div className="foc-content flex flex-col overflow-hidden">
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-50/40 p-5">
        <TableShell
          className="fleet-table-shell"
          contentClassName="flex h-full min-h-0 min-w-0 gap-4 overflow-hidden bg-slate-50/50 p-4"
          header={
            <header className="flex flex-nowrap items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 py-4">
              <div className="focusoc-form-shell flex min-w-0 flex-nowrap items-center gap-3">
                <label className="flex min-w-[220px] items-center gap-4">
                  <span className="text-xs font-semibold uppercase text-slate-500">
                    Fleet Status
                  </span>
                  <SelectMenu
                    disabled={sortedOrganizations.length === 0}
                    onChange={setSelectedOrganization}
                    options={sortedOrganizations}
                    value={selectedOrganization ?? ""}
                  />
                </label>

                <div className="flex flex-nowrap items-center gap-2 text-xs font-semibold uppercase">
                  <span className="whitespace-nowrap rounded-full bg-red-50 px-2.5 py-1 text-red-700 ring-1 ring-inset ring-red-200">
                    {riskSummary.alerts} alert
                  </span>
                  <span className="whitespace-nowrap rounded-full bg-amber-50 px-2.5 py-1 text-amber-700 ring-1 ring-inset ring-amber-200">
                    {riskSummary.warnings} warning
                  </span>
                  <span className="whitespace-nowrap rounded-full bg-slate-100 px-2.5 py-1 text-slate-600 ring-1 ring-inset ring-slate-200">
                    {riskSummary.info} info
                  </span>
                </div>
              </div>

              <div className="focusoc-form-shell flex flex-nowrap items-center justify-end gap-3">
                <div className="flex items-center gap-4">
                  <span className="text-xs font-semibold uppercase text-slate-500">
                    View
                  </span>

                  <div
                    aria-label="Fleet status view"
                    className="inline-flex h-9 flex-nowrap items-center rounded-lg border border-slate-200 bg-slate-50 p-1"
                    role="group"
                  >
                    {viewModeOptions.map((option) => {
                      const isSelected = viewMode === option.value;

                      return (
                        <button
                          aria-pressed={isSelected}
                          className={`inline-flex h-7 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-sm font-semibold transition ${
                            isSelected
                              ? "bg-white text-slate-950 shadow-sm"
                              : "text-slate-500 hover:text-slate-950"
                          }`}
                          key={option.value}
                          onClick={() => setViewMode(option.value)}
                          title={`Show ${option.label.toLowerCase()} view`}
                          type="button"
                        >
                          <Icon icon={option.icon} size={14} title={false} />
                          <span>{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </header>
          }
        >
          {contactsSelection && (
            <ConjunctionContactsPanel
              onClose={() => setContactsSelection(null)}
              selection={contactsSelection}
              variant="card"
            />
          )}

          <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
            <StatusProxy status={combinedStatus} error={combinedError}>
              {viewMode === "table" ? (
                <Table
                  actions={fleetTableActions}
                  base="fleet"
                  columnDefinitions={columnDefinitions}
                  controlledColumnFilter={fleetFilter}
                  controlledColumnFilterChange={handleFleetFilterChange}
                  defaultVisibleValues={CONJ_DEFAULT_VISIBLE_COLUMNS}
                  downloadData={handleDownload}
                  height="auto"
                  rowClassRules={{
                    "!bg-red-50": (params) =>
                      params.original.riskLevel.value === RiskLevel.ALERT,
                    "!bg-amber-50": (params) =>
                      params.original.riskLevel.value === RiskLevel.WARNING,
                  }}
                  rowData={fleetConjunctionItems}
                  saveColumnLayout={saveColumnLayout}
                  serverDisplayConfig={savedColumnsLayout.items}
                  showGlobalFilter
                  showOperatorColumn
                  showSidebar
                />
              ) : (
                <FleetStatusCardList
                  conjunctions={fleetConjunctionItems}
                  formatters={formatters}
                  riskConfig={currentConfig as any}
                  onContactsSelect={handleContactsSelection}
                  onDownload={handleDownload}
                />
              )}
            </StatusProxy>
          </div>
        </TableShell>
      </main>
    </div>
  );
};
